import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DoorOpen, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";

import { ApiRequestError, apiRequest } from "../../common/api/apiClient.js";
import { useActiveMembership } from "../../common/membership/ActiveMembership.js";
import type { components } from "../../generated/api.js";

type VenueDTO = components["schemas"]["VenueDTO"];

/**
 * Orte eines Unternehmens: Kinosäle, Behandlungsräume, Stationen.
 *
 * Ein Saal mit Sitzplan macht Platzwahl möglich; einer ohne zählt nur Köpfe. Deshalb
 * steht die Reihenform hier und nicht erst beim Angebot — ein Saal wird einmal
 * eingerichtet und dann hundertmal bespielt.
 */
export function VenuesPage(): JSX.Element {
  const { can } = useActiveMembership();
  const queryClient = useQueryClient();
  const [offen, setOffen] = useState(false);

  const venues = useQuery({
    queryKey: ["venues"],
    queryFn: () => apiRequest<VenueDTO[]>("/venues"),
  });

  const entfernen = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/venues/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["venues"] }),
  });

  const liste = venues.data ?? [];

  return (
    <div className="px-5 pb-8">
      <div className="flex flex-wrap items-center gap-3 pt-5">
        <h1 className="flex-1 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Orte</h1>
        {can("venue.manage") && (
          <button type="button" className="btn sm" onClick={() => setOffen(true)}>
            Ort anlegen
          </button>
        )}
      </div>

      {venues.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}

      {liste.length === 0 && !venues.isPending && (
        <p className="mt-5 max-w-[60ch] text-[14px] leading-relaxed text-ink-2">
          Noch kein Ort angelegt. Ein Saal mit Sitzplan erlaubt später die Platzwahl; ohne
          Sitzplan zählt allein die Kapazität.
        </p>
      )}

      <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
        {liste.map((venue) => (
          <li key={venue.id} className="card pad flex items-start gap-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-sm bg-brand-soft text-role">
              <DoorOpen size={18} aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-bold text-ink">{venue.name}</p>
              <p className="num mt-0.5 text-[12.5px] text-ink-2">
                {venue.capacity} Plätze
                {venue.rows ? ` · ${venue.rows.length} Reihen` : " · freie Platzwahl"}
              </p>
              {venue.rows && (
                <p className="num mt-1 text-[11px] text-ink-3">
                  {venue.rows.map((row) => `${row.label}${row.premium ? "*" : ""}`).join(" ")}
                  {venue.rows.some((row) => row.premium) && "   * Premium"}
                </p>
              )}
            </div>
            {can("venue.manage") && (
              <button
                type="button"
                className="grid h-9 w-9 flex-none place-items-center rounded-sm text-ink-3 hover:text-danger"
                onClick={() => entfernen.mutate(venue.id)}
                aria-label={`${venue.name} löschen`}
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {entfernen.isError && (
        <p className="mt-3 text-[13px] text-danger">
          {entfernen.error instanceof ApiRequestError
            ? entfernen.error.message
            : "Der Ort liess sich nicht löschen."}
        </p>
      )}

      {offen && <VenueDialog onClose={() => setOffen(false)} />}
    </div>
  );
}

function VenueDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [mitSitzplan, setMitSitzplan] = useState(true);
  const [reihen, setReihen] = useState(10);
  const [proReihe, setProReihe] = useState(14);
  const [premiumAb, setPremiumAb] = useState(8);
  const [kapazitaet, setKapazitaet] = useState(80);

  const anlegen = useMutation({
    mutationFn: () =>
      apiRequest<VenueDTO>("/venues", {
        method: "POST",
        body: mitSitzplan
          ? {
              name,
              rows: Array.from({ length: reihen }, (_, index) => ({
                label: String.fromCharCode(65 + index),
                seats: proReihe,
                premium: index + 1 >= premiumAb,
              })),
            }
          : { name, capacity: kapazitaet },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["venues"] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-40 grid place-items-end bg-ink/40 sm:place-items-center">
      <div className="w-full max-w-md rounded-xl bg-surface p-5 shadow-3 sm:rounded-lg">
        <h2 className="text-[17px] font-extrabold tracking-[-0.02em] text-ink">Ort anlegen</h2>

        <label className="micro mt-4 block" htmlFor="venue-name">
          Name
        </label>
        <input
          id="venue-name"
          className="input mt-1.5"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Saal 1 · Großer Saal"
        />

        <label className="mt-4 flex items-center gap-2.5 text-[14px] font-semibold text-ink">
          <input
            type="checkbox"
            checked={mitSitzplan}
            onChange={(event) => setMitSitzplan(event.target.checked)}
          />
          Sitzplan — Gäste wählen ihren Platz
        </label>

        {mitSitzplan ? (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Zahl label="Reihen" value={reihen} onChange={setReihen} />
            <Zahl label="Plätze/Reihe" value={proReihe} onChange={setProReihe} />
            <Zahl label="Premium ab" value={premiumAb} onChange={setPremiumAb} />
          </div>
        ) : (
          <div className="mt-3">
            <Zahl label="Kapazität" value={kapazitaet} onChange={setKapazitaet} />
          </div>
        )}

        {mitSitzplan && (
          <p className="num mt-3 text-[12.5px] text-ink-2">
            Ergibt {reihen * proReihe} Plätze.
          </p>
        )}

        {anlegen.isError && (
          <p className="mt-3 text-[13px] text-danger">
            {anlegen.error instanceof ApiRequestError
              ? anlegen.error.message
              : "Der Ort liess sich nicht anlegen."}
          </p>
        )}

        <div className="mt-5 flex gap-2.5">
          <button type="button" className="btn ghost flex-1" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn flex-1"
            disabled={name.trim().length === 0 || anlegen.isPending}
            onClick={() => anlegen.mutate()}
          >
            {anlegen.isPending ? "Wird angelegt …" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Zahl({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}): JSX.Element {
  const id = `zahl-${label.replace(/\W/g, "")}`;
  return (
    <div>
      <label className="micro block" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={1}
        className="input num mt-1.5"
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}
